import {
  FileRecord,
  RawFileType,
} from '@packages/systems/core/records/FileRecord';
import {AssetState} from '@packages/systems/designer/assets';
import {findByFileHash} from '@packages/shared/utils/AssetUtils';
import {isLibraryAsset} from '@packages/domains/assets/utilities';
import {AssetManagerUIState} from '@packages/systems/assetManager';
import {LibraryExport} from '@packages/systems/libraries';
import {Dispatch} from '@packages/systems/flux';
import {Asset, Plugins} from '@packages/systems/wfdl';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {libraryAssetEngineStarted} from '../flux/actionCreators';

// Uses Web Crypto API to generate an ObjectId-compatible hex string,
// avoiding a BSON runtime dependency in the designer bundle.
function generateObjectId(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(12)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function findLibraryAssetLocalCopy(
  assets: AssetState['assets'],
  asset: FileRecord
) {
  return findByFileHash({
    ...asset.toJS(),
    origin: qualify(
      Plugins.name(String(asset.versionedResource)),
      Asset.name(asset._id)
    ),
  })(assets);
}

export function resolveLibraryAssetLocalCopy({
  assets,
  asset,
}: {
  assets: AssetState['assets'];
  asset: FileRecord;
}): {
  localCopy: FileRecord;
  assetsToImport: {[key: string]: FileRecord} | null;
} {
  if (!isLibraryAsset(asset)) {
    return {localCopy: asset, assetsToImport: null};
  }

  const existingLocalCopy = findLibraryAssetLocalCopy(assets, asset);
  if (existingLocalCopy) {
    return {localCopy: existingLocalCopy, assetsToImport: null};
  }

  const newId = generateObjectId();

  const localCopy = asset
    .set('_id', newId)
    .set(
      'origin',
      qualify(
        Plugins.name(String(asset.versionedResource)),
        Asset.name(asset._id)
      )
    )
    .set('versionedResource', null)
    .set('sharedResourceProfile', null);

  return {
    localCopy,
    assetsToImport: {
      [asset._id]: localCopy,
    },
  };
}

export function handleLibraryAssetLocalCopy({
  dispatch,
  assets,
  asset,
}: {
  dispatch: Dispatch;
  assets: AssetState['assets'];
  asset: FileRecord;
}) {
  const {localCopy, assetsToImport} = resolveLibraryAssetLocalCopy({
    assets,
    asset,
  });

  if (assetsToImport) {
    dispatch(
      libraryAssetEngineStarted({
        assetsToImport,
      })
    );
  }

  return localCopy;
}

export function reconstructFoldersFromFolderPaths({
  pluginAssets,
  pluginId,
  exports,
  datePublished,
}: {
  pluginAssets: RawFileType[];
  pluginId: string;
  exports: LibraryExport<string>[];
  datePublished?: string | null;
}): AssetManagerUIState['assetManagerFolders'] {
  // Map to store folder structures where the key is the full string
  // folder path and value is the folder object
  const folderMap: Record<
    string,
    AssetManagerUIState['assetManagerFolders'][number]
  > = {};

  // Create a Set of asset IDs that are explicitly exported from the library
  // Only include assets that are marked as type 'Asset' in the exports
  const assetsWithExportIds = new Set(
    exports
      .filter((exp) => exp.type === 'Asset')
      .map((exp) => exp.metadata.targetId)
  );

  pluginAssets
    // Process only files that are part of the library exports
    .filter((file) => assetsWithExportIds.has(file._id))
    .forEach((file) => {
      // Only process files that have a folder path defined
      if (!file.folderPath || !file.folderPath.length) {
        return;
      }

      // Track the full path as we traverse the folder structure
      let currentPath = '';
      // Keep reference to parent folder for building hierarchy
      let parentId: string | null = null;

      // Iterate through each segment of the folder path
      // e.g., for path "images/icons/social", we process: "images", then "icons", then "social"
      file.folderPath.forEach(({id, name}, index) => {
        // Build the cumulative path for this folder level
        currentPath = currentPath ? `${currentPath}/${id}` : id;

        // Create folder in our map if it doesn't exist yet
        const existingFolder = folderMap[currentPath];
        if (!existingFolder) {
          folderMap[currentPath] = {
            _id: id,
            name,
            parent: parentId,
            members: [], // Array to store file IDs that belong to this folder
            versionedResourceId: pluginId,
            createdOn: datePublished ? new Date(datePublished) : new Date(),
          };
        }

        // If this is the last segment of the path (the actual folder containing the file),
        // add the file to the folder's members
        if (file.folderPath && index === file.folderPath.length - 1) {
          folderMap[currentPath]?.members.push(file._id);
        }

        // Update parent reference for next iteration
        parentId = folderMap[currentPath]?._id || null;
      });
    });

  // Convert the folder map into an array of folder objects
  return Object.values(folderMap);
}

/**
 * Organizes library folders into a structured format by grouping them by library ID
 * and tracking root folder names.
 *
 * @returns An object containing:
 *   - rootNames: Map of library IDs to their root folder names
 *   - byLibrary: Map of library IDs to arrays of their associated folders
 */
export function parseLibraryFolders<
  T extends {versionedResourceId?: string; _id: string; name: string},
>({libraryFolders}: {libraryFolders: T[]}) {
  return libraryFolders.reduce<Record<string, T[]>>((acc, folder) => {
    const libraryId = folder.versionedResourceId;
    if (!libraryId) return acc;

    // Handle folders by library index
    acc[libraryId] = acc[libraryId] || [];
    acc[libraryId].push(folder);

    return acc;
  }, {});
}
